import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import { RootStore } from "src/stores/RootStore";
import {
    INVOICE_FILE_STATE,
    INVOICE_LIST_STATE,
    INVOICE_STATE,
    INVOICE_TYPE,
    SORT,
    WORKFLOWUSER_ROLE
} from "./InvoiceEnums";
import {
    IAssignedUser,
    ICreditor,
    IInvoiceFile,
    IInvoiceFilter,
    IInvoiceProperties,
    IRealestate,
    ISearchTag
} from "./InvoiceTypes";
import { GetRealestateUserAndAccessData_realestateUsersAndAccess } from "src/api/generated/GetRealestateUserAndAccessData";
import { format } from "date-fns";
import i18next from "i18next";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { Route, selectRoute } from "src/config/routes";
import Axios from "src/network/Axios";
import {
    GetAllInvoices_cred_invoices_insertuserinfo,
    GetAllInvoices_cred_invoices_workflowusers
} from "src/api/generated/GetAllInvoices";
import { GET_WORKFLOW_IDS, SEARCH_INVOICES } from "src/api/cred";
import { SearchInvoice, parseSearchInvoices } from "src/types/searchInvoices";
import { SearchInvoices, SearchInvoicesVariables } from "src/api/generated/SearchInvoices";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { Invoice } from "./InvoiceStore";
import {
    GetInvoiceByNumber_cred_invoices_insertuserinfo,
    GetInvoiceByNumber_cred_invoices_workflowusers
} from "src/api/generated/GetInvoiceByNumber";
import { GetWorkflowIds, GetWorkflowIdsVariables } from "src/api/generated/GetWorkflowIds";

type BatchProcessMap = {
    [invoiceNumber: number]: boolean;
};

type ListStateSelectedMap = {
    [K in INVOICE_LIST_STATE]: boolean;
};

const INVOICE_LIST_REFRESH_TIME_IN_SECONDS = 300;

export const getInvoiceStateById = (stateId: number): INVOICE_STATE => {
    switch (stateId) {
        case 0:
            return INVOICE_STATE.UNDEFINED;
        case 10:
            return INVOICE_STATE.NEW;
        case 15:
            return INVOICE_STATE.ASSIGN_REALESTATE;
        case 20:
            return INVOICE_STATE.ASSIGN_WORKFLOW_USERS;
        case 25:
            return INVOICE_STATE.RESTART_AFTER_REJECTED;
        case 30:
            return INVOICE_STATE.READY_FOR_VISA;
        case 41:
            return INVOICE_STATE.VISA_1_OK;
        case 42:
            return INVOICE_STATE.VISA_2_OK;
        case 43:
            return INVOICE_STATE.VISA_3_OK;
        case 49:
            return INVOICE_STATE.FULLY_SIGHTED;
        case 50:
            return INVOICE_STATE.SENT_TO_ERP;
        case 60:
            return INVOICE_STATE.IMPORTED_IN_ERP;
        case 90:
            return INVOICE_STATE.PAID;
        case 95:
            return INVOICE_STATE.REJECTED;
        case 99:
            return INVOICE_STATE.CANCELLED;
        default:
            return INVOICE_STATE.NEW;
    }
};

export class InvoiceListStore {
    rootStore: RootStore;

    invoices: Invoice[] = [];

    invoiceFilter: IInvoiceFilter = {
        state: INVOICE_LIST_STATE.NEW,
        sort: SORT.DATE_NEWEST,
        query: "",
        types: [],
        showOnlyMyInvoices: true,
        showOnlyInvoicesFromMyRealestates: false
    };

    searchTags: ISearchTag[] = [
        {
            label: i18next.t("screens.kredi_flow.list.tags.only_mine"),
            id: "SHOW_ONLY_MY_OWN_INVOICES",
            active: this.invoiceFilter.showOnlyMyInvoices
        },
        {
            label: i18next.t("screens.kredi_flow.list.tags.my_realestates"),
            id: "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES",
            active: false
        },
        { label: i18next.t("screens.kredi_flow.invoice_type.1"), id: "2", active: false, type: INVOICE_TYPE.INVOICE },
        { label: i18next.t("screens.kredi_flow.invoice_type.2"), id: "3", active: false, type: INVOICE_TYPE.CREDIT }
    ];

    realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[] = [];

    loadAllInvoices: boolean = false;

    batchProcessMap: BatchProcessMap = {};

    hasBatchProcessMapInitialized: boolean = false;

    areAllInvoicesOfStateSelected: ListStateSelectedMap = {
        [INVOICE_LIST_STATE.NEW]: false,
        [INVOICE_LIST_STATE.TO_APPROVE]: false,
        [INVOICE_LIST_STATE.PAY]: false,
        [INVOICE_LIST_STATE.PAID]: false,
        [INVOICE_LIST_STATE.REJECTED]: false,
        [INVOICE_LIST_STATE.CANCELLED]: false,
        [INVOICE_LIST_STATE.ALL]: false,
        [INVOICE_LIST_STATE.ALL_OPEN]: false,
        [INVOICE_LIST_STATE.ADDITIONAL_INVOICES]: false,
        [INVOICE_LIST_STATE.IMPORT_DOCUMENTS]: false
    };

    isBatchProcessing: boolean = false;

    currentBatchProcessIndex: number = 0;

    isDownloadingInvoiceFiles: boolean = false;

    isDeletingInvoices: boolean = false;

    initialLoadInvoicesCompleted: boolean = false;

    isLoadingInvoices: boolean = false;

    loadInvoicesTimer?: NodeJS.Timer;
    lastReloadTime?: Date;
    currentTime: Date = new Date();

    // Server-side search and filter parameters
    searchQuery: string = "";
    stateFilter: string = "{10,15,20,25}"; // Default: NEW states
    sortField: string = "number";
    sortDirection: string = "desc";
    pageLimit: number = 100;
    pageOffset: number = 0;
    typeFilters: string | null = null;
    unitId: string | null = null;
    assigneduserIds: string | null = null;
    realestateIds: string | null = null;
    creditorIds: string | null = null;
    startDate: string | null = null;
    endDate: string | null = null;
    filterRealestatesByUserIds: string | null = null;
    totalInvoiceCount: number = 0;
    invoiceStateCounts: {
        new: number;
        to_approve: number;
        pay: number;
        paid: number;
        rejected: number;
        cancelled: number;
    } = { new: 0, to_approve: 0, pay: 0, paid: 0, rejected: 0, cancelled: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            invoiceFilter: observable,
            realestateUserData: observable,
            searchTags: observable,
            loadAllInvoices: observable,
            batchProcessMap: observable,
            areAllInvoicesOfStateSelected: observable,
            isBatchProcessing: observable,
            currentBatchProcessIndex: observable,
            hasBatchProcessMapInitialized: observable,
            isDownloadingInvoiceFiles: observable,
            isDeletingInvoices: observable,
            lastReloadTime: observable,
            currentTime: observable,
            loadInvoicesTimer: observable,
            initialLoadInvoicesCompleted: observable,
            invoices: observable,
            isLoadingInvoices: observable,
            // Server-side search parameters
            searchQuery: observable,
            stateFilter: observable,
            sortField: observable,
            sortDirection: observable,
            pageLimit: observable,
            pageOffset: observable,
            typeFilters: observable,
            unitId: observable,
            assigneduserIds: observable,
            realestateIds: observable,
            creditorIds: observable,
            startDate: observable,
            endDate: observable,
            filterRealestatesByUserIds: observable,
            totalInvoiceCount: observable,
            invoiceStateCounts: observable,

            setFilter: action,
            setFilterState: action,
            clearFilter: action,
            setTag: action,
            setRealestateUserData: action,
            setLoadAllInvoices: action,
            setBatchProcessMapEntry: action,
            toggleSelectAllInvoicesOfState: action,
            setIsBatchProcessing: action,
            setNextBatchProcessIndex: action,
            setPreviousBatchProcessIndex: action,
            setBatchProcessMapInitialized: action,
            terminateBatchProcess: action,
            setCurrentBatchProcessIndex: action,
            downloadInvoiceFilesZip: action,
            deleteInvoices: action,
            navigateToNextBatchProcess: action,
            setIsDownloadingInvoiceFiles: action,
            setIsDeletingInvoices: action,
            handleBatchProcessCheckboxClick: action,
            deselectAllInvoices: action,
            resetAreAllInvoicesOfStateSelected: action,
            setLastReloadTime: action,
            updateCurrentTime: action,
            loadInvoices: action,
            initialLoadInvoices: action,
            setInvoices: action,
            setInitialLoadInvoicesCompleted: action,
            clearLoadInvoicesTimer: action,
            startLoadInvoicesTimer: action,
            setIsLoadingInvoices: action,
            // Server-side search setters
            setSearchQuery: action,
            setStateFilter: action,
            setSortField: action,
            setSortDirection: action,
            setPageLimit: action,
            setPageOffset: action,
            setTypeFilters: action,
            setUnitId: action,
            setAssignedUserIds: action,
            setRealestateIds: action,
            setCreditorIds: action,
            setStartDate: action,
            setEndDate: action,
            setFilterRealestatesByUserIds: action,

            filteredInvoices: computed,
            currentTabInvoiceCount: computed,
            selectedBatchProcessInvoiceNumbers: computed,
            hasAnySelectedBatchProcessInvoice: computed,
            nextBatchProcessInvoiceNumber: computed,
            previousBatchProcessInvoiceNumber: computed,
            selectedFileIds: computed,
            selectedInvoiceIds: computed,
            lastReloadTimeText: computed
        });

        setInterval(() => {
            this.updateCurrentTime();
        }, 30000); // Update every 30 seconds
    }

    setIsDownloadingInvoiceFiles = (isDownloadingInvoiceFiles: boolean) => {
        this.isDownloadingInvoiceFiles = isDownloadingInvoiceFiles;
    };

    setIsDeletingInvoices = (isDeletingInvoices: boolean) => {
        this.isDeletingInvoices = isDeletingInvoices;
    };

    setIsLoadingInvoices = (isLoadingInvoices: boolean) => {
        this.isLoadingInvoices = isLoadingInvoices;
    };

    // Server-side search setters
    setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    setStateFilter = (filter: string) => {
        this.stateFilter = filter;
    };

    setSortField = (field: string) => {
        this.sortField = field;
    };

    setSortDirection = (direction: string) => {
        this.sortDirection = direction;
    };

    setPageLimit = (limit: number) => {
        this.pageLimit = limit;
    };

    setPageOffset = (offset: number, reload: boolean = true) => {
        this.pageOffset = offset;
        if (reload) {
            this.loadInvoices();
        }
    };

    setTypeFilters = (types: string | null) => {
        this.typeFilters = types;
    };

    setUnitId = (unitId: string | null) => {
        this.unitId = unitId;
    };

    setAssignedUserIds = (assigneduserIds: string | null) => {
        this.assigneduserIds = assigneduserIds;
    };

    setRealestateIds = (realestateIds: string | null) => {
        this.realestateIds = realestateIds;
    };

    setCreditorIds = (creditorIds: string | null) => {
        this.creditorIds = creditorIds;
    };

    setStartDate = (startDate: string | null) => {
        this.startDate = startDate;
    };

    setEndDate = (endDate: string | null) => {
        this.endDate = endDate;
    };

    setFilterRealestatesByUserIds = (userIds: string | null) => {
        this.filterRealestatesByUserIds = userIds;
    };

    setBatchProcessMapEntry = (invoiceNumber: number, batchProcess: boolean) => {
        this.batchProcessMap[invoiceNumber] = batchProcess;
    };

    getBatchProcessMapEntry = (invoiceNumber: number) => {
        return this.batchProcessMap[invoiceNumber] ?? false;
    };

    convertInvoiceStateToInvoiceListState = (state: INVOICE_STATE): INVOICE_LIST_STATE => {
        const invoiceStateNumber = Number(state);
        let convertedInvoiceListFilterState = INVOICE_LIST_STATE.ALL;

        switch (true) {
            case invoiceStateNumber >= INVOICE_STATE.NEW && invoiceStateNumber <= INVOICE_STATE.RESTART_AFTER_REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.NEW;
                break;
            case invoiceStateNumber >= INVOICE_STATE.READY_FOR_VISA && invoiceStateNumber <= INVOICE_STATE.VISA_3_OK:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.TO_APPROVE;
                break;
            case invoiceStateNumber >= INVOICE_STATE.FULLY_SIGHTED &&
                invoiceStateNumber <= INVOICE_STATE.IMPORTED_IN_ERP:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAY;
                break;
            case invoiceStateNumber === INVOICE_STATE.PAID:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAID;
                break;
            case invoiceStateNumber === INVOICE_STATE.REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.REJECTED;
                break;
            case invoiceStateNumber === INVOICE_STATE.CANCELLED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.CANCELLED;
                break;
        }

        return convertedInvoiceListFilterState;
    };

    clearFilter = () => {
        this.searchTags = [...this.searchTags].map((t) => {
            return { ...t, active: false };
        });

        this.setFilter({
            state: this.invoiceFilter.state, // when clearing the filter, keep the state (stay in the same tab)
            sort: SORT.DATE_NEWEST,
            query: "",
            types: [],
            showOnlyMyInvoices: false,
            showOnlyInvoicesFromMyRealestates: false
        });
    };

    setTag = (id: string, active: boolean) => {
        const newSearchTags: ISearchTag[] = [...this.searchTags].map((searchTag) => {
            if (searchTag.id === id) {
                return { ...searchTag, active: active };
            }
            return searchTag;
        });

        const activeSearchTags: INVOICE_TYPE[] = [];

        newSearchTags.forEach((tag) => {
            if (tag.active && tag.type) {
                activeSearchTags.push(tag.type);
            }
        });

        this.searchTags = newSearchTags;

        this.setFilter({
            ...this.invoiceFilter,
            types: activeSearchTags,
            showOnlyMyInvoices:
                newSearchTags.find((searchTag) => searchTag.id === "SHOW_ONLY_MY_OWN_INVOICES")?.active ?? false,
            showOnlyInvoicesFromMyRealestates:
                newSearchTags.find((st) => st.id === "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES")?.active ?? false
        });
    };

    // Setters
    setFilter = (newFilter: IInvoiceFilter) => {
        this.invoiceFilter = newFilter;
    };

    setFilterState = (state: INVOICE_LIST_STATE) => {
        this.setFilter({ ...this.invoiceFilter, state });
    };

    setRealestateUserData = (realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[]) => {
        this.realestateUserData = realestateUserData;
    };

    setLoadAllInvoices = (loadAllInvoices: boolean) => {
        this.loadAllInvoices = loadAllInvoices;
    };

    toggleSelectAllInvoicesOfState = (invoiceListState: INVOICE_LIST_STATE) => {
        const selected = this.areAllInvoicesOfStateSelected[invoiceListState] ?? false;
        const newSelected = !selected;

        if (invoiceListState === INVOICE_LIST_STATE.ALL) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.PAID]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.CANCELLED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected,
                [INVOICE_LIST_STATE.ALL]: newSelected
            };
        } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected
            };
        } else {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [invoiceListState]: newSelected
            };
        }

        const invoices = this.filteredInvoices.filter((invoice) => {
            if (invoiceListState === INVOICE_LIST_STATE.ALL) {
                return true;
            } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
                return !(invoice.state === INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.CANCELLED);
            } else {
                let convertedInvoiceListFilterState: INVOICE_LIST_STATE | undefined = undefined;

                const invoiceState: INVOICE_STATE | undefined = invoice.state;
                if (invoiceState) {
                    convertedInvoiceListFilterState = this.convertInvoiceStateToInvoiceListState(invoiceState);
                }

                const currentInvoiceListFilterState: INVOICE_LIST_STATE = invoiceListState;

                return convertedInvoiceListFilterState === currentInvoiceListFilterState;
            }
        });

        invoices.forEach((invoice) => {
            this.setBatchProcessMapEntry(invoice.number ?? 0, newSelected);
        });
    };

    deselectAllInvoices = () => {
        this.batchProcessMap = {};
        this.hasBatchProcessMapInitialized = false;
        this.isBatchProcessing = false;
        this.currentBatchProcessIndex = 0;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });
    };
    resetAreAllInvoicesOfStateSelected = () => {
        const resetState: ListStateSelectedMap = {} as ListStateSelectedMap;

        // Loop through all enum values to ensure all possible states are reset
        Object.values(INVOICE_LIST_STATE).forEach((state) => {
            // Only process numeric enum values (skip reverse mappings)
            if (typeof state === "number") {
                resetState[state] = false;
            }
        });

        this.areAllInvoicesOfStateSelected = resetState;
    };

    setIsBatchProcessing = (isBatchProcessing: boolean) => {
        this.isBatchProcessing = isBatchProcessing;
    };

    setCurrentBatchProcessIndex = (currentBatchProcessIndex: number) => {
        this.currentBatchProcessIndex = currentBatchProcessIndex;
    };

    setNextBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        this.currentBatchProcessIndex = nextBatchProcessIndex;
    };

    setPreviousBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        this.currentBatchProcessIndex = previousBatchProcessIndex;
    };

    setBatchProcessMapInitialized = (hasBatchProcessMapInitialized: boolean) => {
        this.hasBatchProcessMapInitialized = hasBatchProcessMapInitialized;
    };

    terminateBatchProcess = () => {
        this.isBatchProcessing = false;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });

        this.currentBatchProcessIndex = 0;
        this.hasBatchProcessMapInitialized = false;
    };

    downloadInvoiceFiles = async () => {
        this.setIsDownloadingInvoiceFiles(true);

        const selectedFileIds: string[] = this.selectedFileIds;

        if (selectedFileIds.length === 1) {
            const fileIdToDownload = selectedFileIds[0];
            let fileName = undefined;
            const invoice = this.invoices.find((i) => i.invoiceFile?.fileId === fileIdToDownload);

            // invoice date in yyyyMMdd format -> 15.05.2025 = 20250515
            const invoiceDate = invoice?.date ? format(new Date(invoice.date), "yyyyMMdd") : "";
            if (invoice) {
                fileName = `${invoice.number ?? ""}-${invoice.creditor?.name ?? ""}-${invoiceDate}-${
                    invoice.invoiceNumber ?? ""
                }.pdf`;
            }

            await this.rootStore.fileStore.downloadFileAndTriggerDownload(fileIdToDownload, fileName);
        } else if (selectedFileIds.length > 1) {
            await this.downloadInvoiceFilesZip();
        }

        this.setIsDownloadingInvoiceFiles(false);
    };

    deleteInvoices = async () => {
        this.setIsDeletingInvoices(true);

        const selectedInvoiceIds = this.selectedInvoiceIds;

        if (selectedInvoiceIds.length > 0) {
            const { data }: { data: GetWorkflowIds } = await apolloClientInstance.query<
                GetWorkflowIds,
                GetWorkflowIdsVariables
            >({
                query: GET_WORKFLOW_IDS,
                variables: {
                    invoiceIds: selectedInvoiceIds
                }
            });

            if (data.invoices && data.invoices.length > 0) {
                for (const invoice of data.invoices) {
                    await this.rootStore.restartCancelInvoiceStore.cancelInvoice(
                        invoice.id,
                        invoice.workflowinstance,
                        true
                    );
                }
                await this.setLoadAllInvoices(true);
                await this.loadInvoices();
                this.rootStore.uiStore.printStatusMessage(
                    data.invoices.length === 1
                        ? i18next.t("screens.kredi_flow.action.delete_invoice.success")
                        : i18next.t("screens.kredi_flow.action.delete_invoices.success"),
                    MessageType.SUCCESS
                );
            }
        }

        this.setIsDeletingInvoices(false);
    };

    downloadInvoiceFilesZip = async (): Promise<any | undefined> => {
        let getInvoiceFilesZipBlob = undefined;

        try {
            const role = this.rootStore.authStore.user?.role;

            const requestBody = {
                files: this.selectedFileIds
            };

            const getInvoiceFilesResponse = await Axios.getAuthInstance().post(
                NetworkConfig.getInvoiceFilesUrl,
                requestBody,
                {
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            const contentDisposition = getInvoiceFilesResponse.headers["Content-Disposition"];

            let fileName = `${format(new Date(), "yyyyMMdd_HHmm")}_${i18next.t(
                "screens.realestate.documents.title"
            )}.zip`;

            if (contentDisposition) {
                // prettier-ignore
                fileName = contentDisposition?.split("filename=")[1]?.replaceAll("\"", "");
            }

            getInvoiceFilesZipBlob = getInvoiceFilesResponse.data;
            this.rootStore.fileStore.triggerDownload(getInvoiceFilesZipBlob, fileName);
        } catch (error) {
            console.error("Error downloading invoice files: ", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("stores.file_store.error_downloading_file"),
                MessageType.ERROR
            );
        }

        return getInvoiceFilesZipBlob;
    };

    navigateToNextBatchProcess = () => {
        this.setNextBatchProcessIndex();

        const route = selectRoute(Route.invoiceDetails, this.rootStore.authStore.user?.role, {
            invoicenumber: this.selectedBatchProcessInvoiceNumbers[this.currentBatchProcessIndex].toString()
        });

        this.rootStore.navStore.navigateTo(route);
    };

    handleBatchProcessCheckboxClick = (invoiceNumber: number) => {
        const isCurrentlyChecked = this.batchProcessMap[invoiceNumber] ?? false;

        const newChecked = !isCurrentlyChecked; // Get the new state directly from the event

        if (newChecked) {
            this.setBatchProcessMapEntry(invoiceNumber, true);
        } else {
            this.setBatchProcessMapEntry(invoiceNumber, false);
        }
    };

    setInvoices = (invoices: Invoice[]) => {
        this.invoices = invoices;
    };

    loadInvoices = async () => {
        this.setIsLoadingInvoices(true);

        // Build filtered realestate IDs with PostgreSQL array format (like in TicketListStore)
        // null = no filter, "" = empty array {} (0 results), "uuid,..." = filter by IDs
        const filteredRealestateIds =
            this.realestateIds === null ? null : `{${this.realestateIds}}`;

        try {
            const { data } = await apolloClientInstance.query<SearchInvoices, SearchInvoicesVariables>({
                query: SEARCH_INVOICES,
                fetchPolicy: "no-cache",
                variables: {
                    input: {
                        customer_id: this.rootStore.authStore.user?.customerid,
                        state_filters: this.stateFilter,
                        lim: this.pageLimit,
                        off: this.pageOffset,
                        search_term: this.searchQuery || null,
                        sort_field: this.sortField,
                        sort_direction: this.sortDirection,
                        type_filters: this.typeFilters,
                        unit_id: this.unitId,
                        assigneduser_ids: this.assigneduserIds,
                        realestate_ids: filteredRealestateIds,
                        creditor_ids: this.creditorIds,
                        start_date: this.startDate,
                        end_date: this.endDate,
                        filter_realestates_by_user_ids: this.filterRealestatesByUserIds
                    }
                }
            });

            if (data?.cred_search_invoices) {
                const result = data.cred_search_invoices;
                const parsedInvoices = parseSearchInvoices(result.invoices);

                const newInvoices: Invoice[] = parsedInvoices.map((invoice) => {
                    const newInvoiceData = this.getInvoiceObjectFromSearchInvoice(invoice);
                    return new Invoice(this.rootStore, newInvoiceData);
                });

                runInAction(() => {
                    this.invoiceStateCounts = {
                        new: result.new || 0,
                        to_approve: result.to_approve || 0,
                        pay: result.pay || 0,
                        paid: result.paid || 0,
                        rejected: result.rejected || 0,
                        cancelled: result.cancelled || 0
                    };
                    this.totalInvoiceCount = result.total || 0;
                });

                this.setInvoices(newInvoices);
            } else {
                this.setInvoices([]);
                runInAction(() => {
                    this.invoiceStateCounts = { new: 0, to_approve: 0, pay: 0, paid: 0, rejected: 0, cancelled: 0 };
                    this.totalInvoiceCount = 0;
                });
            }
        } catch (error) {
            console.error("Error loading invoices:", error);
            this.setInvoices([]);
        }

        this.rootStore.invoiceListStore.setLastReloadTime(new Date());
        this.setIsLoadingInvoices(false);
    };

    initialLoadInvoices = async () => {
        this.setInitialLoadInvoicesCompleted(false);

        // Use the new server-side search query
        await this.loadInvoices();

        const invoiceListStore = this.rootStore.invoiceListStore;

        if (!invoiceListStore.hasBatchProcessMapInitialized) {
            this.invoices.forEach((invoice) => {
                if (invoice.number) {
                    invoiceListStore.setBatchProcessMapEntry(invoice.number, false);
                }
            });

            invoiceListStore.setBatchProcessMapInitialized(true);
        }

        this.setInitialLoadInvoicesCompleted(true);
    };

    getInvoiceObjectFromSearchInvoice = (invoice: SearchInvoice): IInvoiceProperties => {
        // realestate now has zip/city directly (not in houses array)
        const realestate: IRealestate | undefined = invoice.realestate
            ? {
                  id: invoice.realestate.id,
                  number: invoice.realestate.number,
                  name: invoice.realestate.name,
                  zip: invoice.realestate.zip ?? "",
                  city: invoice.realestate.city ?? ""
              }
            : undefined;

        // creditor - id and name can be null
        const creditor: ICreditor | undefined =
            invoice.creditor && invoice.creditor.id
                ? {
                      id: invoice.creditor.id,
                      name: invoice.creditor.name ?? "",
                      number: invoice.creditor.number ?? undefined
                  }
                : undefined;

        // assigned_user comes directly from the backend now
        const assignedUser: IAssignedUser | undefined = invoice.assigned_user
            ? {
                  id: invoice.assigned_user.id,
                  name1: invoice.assigned_user.name1,
                  name2: invoice.assigned_user.name2
              }
            : undefined;

        const invoiceFile: IInvoiceFile | undefined = invoice.fileid
            ? {
                  fileId: invoice.fileid,
                  state: INVOICE_FILE_STATE.LOADED_FROM_DB
              }
            : undefined;

        const newInvoiceData: IInvoiceProperties = {
            id: invoice.id,
            invoiceType: invoice.type as INVOICE_TYPE,
            number: invoice.number,
            state: getInvoiceStateById(invoice.state),
            invoiceNumber: invoice.invoicenumber ?? undefined,
            amount: invoice.amount ?? undefined,
            date: invoice.date,
            dueDate: invoice.duedate,
            paidDate: invoice.paiddate,
            realestate: realestate,
            assignedUser: assignedUser,
            creditor: creditor,
            invoiceFile: invoiceFile
        };

        return newInvoiceData;
    };

    getAssignedUserByInvoiceState = (
        invoiceState: INVOICE_STATE,
        insertUserInfo:
            | GetAllInvoices_cred_invoices_insertuserinfo
            | GetInvoiceByNumber_cred_invoices_insertuserinfo
            | null,
        workflowUsers: GetAllInvoices_cred_invoices_workflowusers[] | GetInvoiceByNumber_cred_invoices_workflowusers[]
    ) => {
        let assignedUser: IAssignedUser | null = null;

        switch (invoiceState) {
            case INVOICE_STATE.NEW:
            case INVOICE_STATE.ASSIGN_REALESTATE:
            case INVOICE_STATE.ASSIGN_WORKFLOW_USERS:
            case INVOICE_STATE.RESTART_AFTER_REJECTED:
                assignedUser = insertUserInfo ?? null;
                break;
            case INVOICE_STATE.READY_FOR_VISA:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_1))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.VISA_1_OK:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_2))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.VISA_2_OK:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_3))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.FULLY_SIGHTED:
            case INVOICE_STATE.SENT_TO_ERP:
            case INVOICE_STATE.IMPORTED_IN_ERP:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.ACCOUNTANT))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.REJECTED:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.REJECTED_TO))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.CANCELLED:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.CANCELLED_BY))
                        ?.user ?? null;
                break;
        }

        return assignedUser;
    };

    startLoadInvoicesTimer = () => {
        this.loadInvoicesTimer = setInterval(() => {
            this.loadInvoices();
        }, INVOICE_LIST_REFRESH_TIME_IN_SECONDS * 1000);
    };

    setInitialLoadInvoicesCompleted = (initialLoadInvoicesCompleted: boolean) => {
        this.initialLoadInvoicesCompleted = initialLoadInvoicesCompleted;
    };

    clearLoadInvoicesTimer = () => {
        if (this.loadInvoicesTimer) {
            clearInterval(this.loadInvoicesTimer);
            this.loadInvoicesTimer = undefined;
        }
    };

    setLastReloadTime = (time: Date) => {
        this.lastReloadTime = time;
    };

    updateCurrentTime = () => {
        this.currentTime = new Date();
    };

    // Computed values
    get invoiceStats() {
        const allInvoices = [...this.filteredInvoices];

        const getNumberOfTicketsByState = (invoiceListState: INVOICE_LIST_STATE) => {
            switch (invoiceListState) {
                case INVOICE_LIST_STATE.NEW:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.NEW &&
                            invoice.state <= INVOICE_STATE.RESTART_AFTER_REJECTED
                    ).length;
                case INVOICE_LIST_STATE.TO_APPROVE:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.READY_FOR_VISA &&
                            invoice.state <= INVOICE_STATE.VISA_3_OK
                    ).length;
                case INVOICE_LIST_STATE.PAY:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.FULLY_SIGHTED &&
                            invoice.state <= INVOICE_STATE.IMPORTED_IN_ERP
                    ).length;
                case INVOICE_LIST_STATE.ALL_OPEN:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            (invoice.state < INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.REJECTED)
                    ).length;
                default:
                    return allInvoices.filter(
                        (invoice) => invoice.state !== undefined && Number(invoice.state) === Number(invoiceListState)
                    ).length;
            }
        };

        return {
            new: getNumberOfTicketsByState(INVOICE_LIST_STATE.NEW),
            toApprove: getNumberOfTicketsByState(INVOICE_LIST_STATE.TO_APPROVE),
            pay: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAY),
            paid: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAID),
            rejected: getNumberOfTicketsByState(INVOICE_LIST_STATE.REJECTED),
            cancelled: getNumberOfTicketsByState(INVOICE_LIST_STATE.CANCELLED),
            allOpen: getNumberOfTicketsByState(INVOICE_LIST_STATE.ALL_OPEN),
            total: { total: this.invoices.length, filtered: allInvoices.length }
        };
    }

    get nextBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;
        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        const nextBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[nextBatchProcessIndex];

        return nextBatchProcessInvoiceNumber;
    }

    get previousBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        const previousBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[previousBatchProcessIndex];

        return previousBatchProcessInvoiceNumber;
    }

    get filteredInvoices() {
        // All filtering is now handled server-side (assigneduser_ids, realestate_ids, type_filters, search_term, sorting)
        // Just return the invoices as-is
        return this.invoices;
    }

    get currentTabInvoiceCount() {
        // Return the count for the currently active tab/state filter (like TicketListStore)
        switch (this.stateFilter) {
            case "{10,15,20,25}": // NEW
                return this.invoiceStateCounts.new;
            case "{30,41,42,43}": // TO_APPROVE
                return this.invoiceStateCounts.to_approve;
            case "{49,50,60}": // PAY
                return this.invoiceStateCounts.pay;
            case "{90}": // PAID
                return this.invoiceStateCounts.paid;
            case "{95}": // REJECTED
                return this.invoiceStateCounts.rejected;
            case "{99}": // CANCELLED
                return this.invoiceStateCounts.cancelled;
            case "{10,15,20,25,30,41,42,43,49,50,60,90,95,99}": // ALL
                return this.totalInvoiceCount;
            case "{10,15,20,25,30,41,42,43,49,50,60,95}": // ALL_OPEN
                return (
                    this.invoiceStateCounts.new +
                    this.invoiceStateCounts.to_approve +
                    this.invoiceStateCounts.pay +
                    this.invoiceStateCounts.rejected
                );
            default:
                return this.totalInvoiceCount;
        }
    }

    get selectedBatchProcessInvoiceNumbers() {
        return Object.entries(this.batchProcessMap)
            .filter(([_, value]) => value === true)
            .map(([invoiceNumber]) => Number(invoiceNumber))
            .sort((a, b) => Number(b) - Number(a));
    }

    get hasAnySelectedBatchProcessInvoice() {
        return Object.entries(this.batchProcessMap).some(([_, value]) => value === true);
    }

    // returns the fileids
    get selectedFileIds() {
        const selectedFileIds: string[] = [];

        this.selectedBatchProcessInvoiceNumbers.forEach((invoiceNumber) => {
            const invoice = this.invoices.find((i) => i.number === invoiceNumber);
            if (invoice && invoice.invoiceFile && invoice.invoiceFile.fileId) {
                selectedFileIds.push(invoice.invoiceFile.fileId);
            }
        });
        return selectedFileIds;
    }

    get selectedInvoiceIds() {
        const selectedInvoiceIds: string[] = [];

        this.selectedBatchProcessInvoiceNumbers.forEach((invoiceNumber) => {
            const invoice = this.invoices.find((i) => i.number === invoiceNumber);
            if (invoice && invoice.id) {
                selectedInvoiceIds.push(invoice.id);
            }
        });
        return selectedInvoiceIds;
    }

    get lastReloadTimeText(): string {
        if (!this.lastReloadTime) {
            return "";
        }

        const diffInMs = this.currentTime.getTime() - this.lastReloadTime.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInMinutes < 1) {
            return "Gerade aktualisiert";
        } else if (diffInMinutes === 1) {
            return "Zuletzt vor 1 Minute aktualisiert";
        } else {
            return `Zuletzt vor ${diffInMinutes} Minuten aktualisiert`;
        }
    }
}
